import '../../i18n/autoTranslate';
import React, { useMemo, useState } from 'react';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import Icon from '../../../components/AppIcon';

const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

const GoalTracker = ({ interviews = [], statistics = null, skillAnalysis = [] }) => {
  const [expanded, setExpanded] = useState(false);

  const last7 = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 6);
    return (interviews || []).filter((i) => {
      if (i?.status !== 'completed' || !i?.completed_at) return false;
      const d = new Date(i.completed_at);
      return d >= new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0);
    });
  }, [interviews]);

  const weeklyTarget = 5;
  const weeklyCount = last7.length;

  const avgScore = useMemo(() => safeNum(statistics?.average_score, 0), [statistics]);
  const bestScore = useMemo(() => safeNum(statistics?.highest_score, 0), [statistics]);

  const weakest = useMemo(() => {
    if (!Array.isArray(skillAnalysis) || !skillAnalysis.length) return null;
    return [...skillAnalysis].sort((a, b) => safeNum(a.score, 0) - safeNum(b.score, 0))[0];
  }, [skillAnalysis]);

  const goals = useMemo(() => {
    const g1 = {
      title: 'Consistency',
      description: `Complete ${weeklyTarget} sessions in 7 days.`,
      icon: 'Calendar',
      current: weeklyCount,
      target: weeklyTarget,
      unit: 'sessions',
      accent: 'from-emerald-400/70 via-sky-400/70 to-violet-400/70',
    };

    const scoreTarget = 85;
    const g2 = {
      title: 'Average score',
      description: `Raise your average to ${scoreTarget}+ by stabilizing structure.`,
      icon: 'TrendingUp',
      current: avgScore,
      target: scoreTarget,
      unit: 'score',
      accent: 'from-violet-400/70 via-sky-400/70 to-emerald-400/70',
    };

    const g3 = weakest
      ? {
          title: `Focus: ${weakest.skill}`,
          description: `Lift your weakest category above 75.`,
          icon: 'Target',
          current: safeNum(weakest.score, 0),
          target: 75,
          unit: 'score',
          accent: 'from-amber-300/70 via-rose-300/50 to-violet-400/70',
        }
      : {
          title: 'Skill signals',
          description: 'Complete a few scored sessions to unlock category goals.',
          icon: 'Sparkles',
          current: 0,
          target: 1,
          unit: 'steps',
          accent: 'from-white/30 via-white/10 to-white/30',
        };

    return [g1, g2, g3];
  }, [weeklyCount, avgScore, weakest]);

  const pct = (current, target) => {
    if (!target) return 0;
    const p = (safeNum(current, 0) / target) * 100;
    return Math.max(0, Math.min(100, Math.round(p)));
  };

  const plan = useMemo(() => {
    const items = [];
    items.push({
      title: 'Use a consistent answer structure',
      desc: 'Start with a 1-sentence summary, then 2–3 bullet points, then a concrete example.',
      icon: 'ListChecks',
    });
    items.push({
      title: 'Train your weakest category',
      desc: weakest ? `Do 2 drills focused on ${weakest.skill}.` : 'Do 2 drills focused on one category per session.',
      icon: 'Brain',
    });
    items.push({
      title: 'Reduce variance',
      desc: bestScore >= 90 ? 'You can hit 90+. Now focus on consistency by rehearsing timing and clarity.' : 'Aim for small daily practice; consistency beats intensity.',
      icon: 'Gauge',
    });
    return items;
  }, [weakest, bestScore]);

  return (
    <SpotlightCard
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]"
      spotlightColor="rgba(16,185,129,0.12)"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -right-24 h-[300px] w-[300px] rounded-full bg-emerald-400/12 blur-[90px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/45" />
      </div>

      <div className="relative z-10 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-white">Goals & next steps</div>
            <div className="text-xs text-white/55 mt-1">A focused plan that turns analytics into improvement.</div>
          </div>

          <button
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold text-white/75 hover:text-white hover:bg-white/[0.06] transition"
          >
            <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
            {expanded ? 'Less' : 'More'}
          </button>
        </div>

        <div className="mt-5 space-y-4">
          {goals.map((g) => {
            const progress = pct(g.current, g.target);
            return (
              <div key={g.title} className="rounded-3xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded-2xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                        <Icon name={g.icon} size={16} className="text-white/75" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{g.title}</div>
                        <div className="text-xs text-white/55">{g.description}</div>
                      </div>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-white">
                      {g.unit === 'sessions' ? `${g.current}/${g.target}` : `${Math.round(safeNum(g.current, 0))}/${g.target}`}
                    </div>
                    <div className="text-[11px] text-white/55">{progress}%</div>
                  </div>
                </div>

                <div className="mt-3 h-2 rounded-full bg-white/8 overflow-hidden">
                  <div className={`h-full rounded-full bg-gradient-to-r ${g.accent}`} style={{ width: `${progress}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {expanded ? (
          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="text-xs font-semibold text-white/70">Recommended plan (this week)</div>
            <div className="mt-3 space-y-3">
              {plan.map((p) => (
                <div key={p.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-8 w-8 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                      <Icon name={p.icon} size={14} className="text-white/70" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-white/80">{p.title}</div>
                      <div className="text-xs text-white/55 mt-1">{p.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </SpotlightCard>
  );
};

export default GoalTracker;
